import { Box, Checkbox, Grid, IconButton } from "@mui/material";
import type { JSX } from "react";
import { useEffect, useMemo, useState } from "react";
import CollapseIcon from "@entrilia/shared/icons/CollapseIcon";
import ExpandIcon from "@entrilia/shared/icons/ExpandIcon";
import { makeTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper.ts";
import AreaFieldItemLoader from "../../../../viewer/common/fields/area-fields/AreaFieldItemLoader.tsx";
import FieldItemCaptions from "../../../../viewer/common/fields/area-fields/FieldItemCaptions.tsx";
import type { ChartOfAccountsContextItem } from "../contexts/ChartOfAccountsContext.ts";
import { useChartOfAccountsContext } from "../contexts/ChartOfAccountsContext.ts";

interface FilterFieldItemProps<T> {
  field: T;
  formatCaption?: (field: T) => JSX.Element | undefined;
  isLoading?: boolean;
  startIcon: JSX.Element;
  onToggle?: (expanded: boolean) => void;
  disableExpanding?: boolean;
  filterId: string;
  parentNode: ChartOfAccountsContextItem | null;
}

export const FilterFieldItem = <T,>({
  field,
  formatCaption,
  isLoading,
  startIcon,
  onToggle,
  disableExpanding,
  parentNode,
  filterId,
}: FilterFieldItemProps<T>) => {
  const [conditionExpanded, setConditionExpanded] = useState(false);
  const { root, appendChild, getNode, onSelect } = useChartOfAccountsContext();

  const currentNode = useMemo(() => {
    if (!parentNode) return undefined;
    return getNode(filterId, parentNode);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [parentNode, filterId, root]);

  useEffect(() => {
    if (!parentNode) return;
    appendChild(filterId, parentNode);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [parentNode, filterId]);

  return (
    <>
      {isLoading === true && <AreaFieldItemLoader />}
      <Grid
        container
        sx={(theme) => ({
          justifyContent: "space-between",
          bgcolor: currentNode?.checked ? makeTintedBackgroundFromColor(theme, theme.palette.primary.main) : "inherit",
          width: "100%",
        })}
      >
        <Grid
          container
          sx={{
            alignItems: "center",
            pl: 1,
            pr: 0.625,
            height: "28px",
            width: "auto",
          }}
        >
          {currentNode && (
            <Checkbox
              indeterminate={currentNode.indeterminate === true}
              checked={currentNode.checked === true}
              sx={{ width: "28px", height: "28px" }}
              onChange={(e) => {
                onSelect(currentNode.identifier, e.target.checked);
              }}
            />
          )}
          <IconButton
            disabled={disableExpanding}
            onClick={() => {
              setConditionExpanded((prev) => {
                onToggle?.(!prev);
                return !prev;
              });
            }}
            sx={{
              width: "28px",
              height: "28px",
              "& .MuiSvgIcon-root": disableExpanding ? { color: "action.disabled" } : {},
            }}
          >
            {conditionExpanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
          <Box sx={{ display: "flex", p: 0.5 }}>{startIcon}</Box>
        </Grid>
        <Grid
          container
          sx={{ alignItems: "center", pl: 0.625, pr: 1, height: "28px", flex: 1, mt: "auto", width: "100%" }}
        >
          <FieldItemCaptions field={field} formatCaption={formatCaption} />
        </Grid>
      </Grid>
    </>
  );
};
